/**
 * useNotificationStream — user-scoped notification SSE (D-W3-5, #518).
 *
 * Connects to GET /api/notifications/stream and forwards `unread-count`
 * pushes (fired by the backend Notification model hook on create /
 * mark-as-read / read-all / delete) so the bell badge updates live instead
 * of on a 120s poll.
 *
 * Auth: the same single-use SSE nonce mechanism as every other EventSource
 * in the app (getSSENonce → ?token=; see useSSEConnection). A fresh nonce
 * is fetched on every (re)connect because nonces are consumed on first use.
 *
 * Reconnect: exponential backoff, capped attempts — mirrors
 * useSSEConnection. The counter resets once a connection is confirmed.
 * A stream that has used up its attempts gets ONE try per return to a
 * visible tab, not a fresh budget (#2382): on 2026-10-08 a rate-limited
 * parent spent six more refused requests on every tab switch.
 *
 * Cancellation (#555 review, finding 1): a generation counter guards every
 * async continuation and timer callback. disconnect() bumps the generation,
 * so a connect() resumed after unmount (mid nonce-fetch) can never open an
 * EventSource nothing owns, and no reconnect timer survives teardown.
 *
 * Liveness watchdog (#555 review, finding 6): the server pings every 30s.
 * If no frame of any kind arrives for 90s the connection is presumed dead
 * (half-open TCP, sleeping laptop, wedged proxy) even though the browser
 * reported no error: close it, flip streamActive off (poll fallback
 * resumes) and reconnect with the usual backoff.
 *
 * Hidden-tab teardown (#555 review, finding 7 — the #504 connection-load
 * class): a tab hidden for over 60s closes its stream. react-query also
 * pauses background polls while hidden, so a hidden tab generates ZERO
 * notification traffic (restores the D-W2-22 invariant). On return to
 * visible the hook reconnects immediately with a fresh attempt budget and
 * fires `onVisible` so the owner can refetch the badge once.
 *
 * Deploy-skew fallback (required by D-W3-5): `streamActive` is true ONLY
 * while the server has confirmed the stream (`connected` event received).
 * NotificationPanel keeps its poll alive whenever `streamActive` is false —
 * so an old backend (stream endpoint 404s), a dropped connection, or
 * exhausted reconnect attempts all degrade to the previous poll behavior
 * instead of losing the badge.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSSENonce } from '@/services/chatApi';
import { API_BASE_URL } from '@/lib/constants';

const MAX_RECONNECT_ATTEMPTS = 5;
const DEFAULT_BASE_RECONNECT_DELAY = 3000;
// Three missed 30s server pings ⇒ presumed dead (#555 review, finding 6).
const WATCHDOG_TIMEOUT = 90_000;
// Hidden-tab grace before tearing the stream down (#555 review, finding 7).
const HIDDEN_CLOSE_DELAY = 60_000;

export interface NotificationStreamOptions {
  /** Called with the fresh unread count on every server push. */
  onUnreadCount: (count: number) => void;
  /** Whether the stream should connect at all. Defaults to true. */
  enabled?: boolean;
  /** Test seam: base backoff delay in ms. Defaults to 3000. */
  baseReconnectDelay?: number;
  /**
   * Called when the tab becomes visible again (#555 review, finding 7) —
   * the owner should refetch the badge once, because refetchOnWindowFocus
   * is globally disabled (main.tsx) and the poll paused while hidden.
   */
  onVisible?: () => void;
}

export function useNotificationStream({
  onUnreadCount,
  enabled = true,
  baseReconnectDelay = DEFAULT_BASE_RECONNECT_DELAY,
  onVisible,
}: NotificationStreamOptions) {
  const [streamActive, setStreamActive] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hiddenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  // Set when the attempt budget runs out; cleared by a confirmed stream
  // (#2382). A visible-tab return then grants one try instead of a budget.
  const gaveUpRef = useRef(false);
  // Generation counter (#555 review, finding 1): disconnect() bumps it, and
  // every async continuation / timer callback that captured an older value
  // bails out instead of resurrecting a connection nobody owns.
  const generationRef = useRef(0);

  // Latest-value refs so listeners never see stale closures.
  const onUnreadCountRef = useRef(onUnreadCount);
  useEffect(() => {
    onUnreadCountRef.current = onUnreadCount;
  }, [onUnreadCount]);
  const onVisibleRef = useRef(onVisible);
  useEffect(() => {
    onVisibleRef.current = onVisible;
  }, [onVisible]);

  // connect() and scheduleReconnect() are mutually recursive — break the
  // cycle with a ref.
  const connectRef = useRef<() => void>(() => {});

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const clearWatchdog = useCallback(() => {
    if (watchdogTimeoutRef.current) {
      clearTimeout(watchdogTimeoutRef.current);
      watchdogTimeoutRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(
    (generation: number) => {
      // Generation-guarded (#555 review, finding 1): no timer may be
      // scheduled after disconnect()/unmount bumped the generation.
      if (generation !== generationRef.current) return;
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        // Give up — streamActive stays false, so the panel's poll remains
        // the badge source (deploy-skew / outage fallback).
        gaveUpRef.current = true;
        return;
      }
      reconnectAttemptsRef.current += 1;
      const delay = baseReconnectDelay * 2 ** (reconnectAttemptsRef.current - 1);
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        if (generation !== generationRef.current) return;
        connectRef.current();
      }, delay);
    },
    [baseReconnectDelay],
  );

  // Liveness watchdog (#555 review, finding 6): re-armed by every frame
  // (connected / unread-count / ping). Firing means 90s of total silence
  // from a server that pings every 30s — treat the stream as dead.
  const armWatchdog = useCallback(
    (generation: number, es: EventSource) => {
      clearWatchdog();
      watchdogTimeoutRef.current = setTimeout(() => {
        watchdogTimeoutRef.current = null;
        if (generation !== generationRef.current) return;
        es.close();
        if (eventSourceRef.current === es) {
          eventSourceRef.current = null;
        }
        setStreamActive(false); // poll fallback resumes immediately
        scheduleReconnect(generation);
      }, WATCHDOG_TIMEOUT);
    },
    [clearWatchdog, scheduleReconnect],
  );

  const connect = useCallback(async () => {
    if (eventSourceRef.current) return;
    const generation = generationRef.current;

    let nonce: string;
    try {
      nonce = await getSSENonce();
    } catch {
      // Nonce fetch failed (expired session, backend down) — retry with
      // backoff; the poll fallback covers the badge meanwhile. The
      // schedule is generation-guarded, so nothing fires past teardown.
      scheduleReconnect(generation);
      return;
    }

    // Stale-generation check after the async gap (#555 review, finding 1):
    // a disconnect()/unmount during the nonce fetch means nobody owns what
    // we would open — bail before constructing the EventSource.
    if (generation !== generationRef.current) return;
    // Re-check the socket too — another connect may have raced ahead.
    if (eventSourceRef.current) return;

    const es = new EventSource(
      `${API_BASE_URL}/api/notifications/stream?token=${nonce}`,
    );
    eventSourceRef.current = es;

    es.addEventListener('connected', () => {
      if (generation !== generationRef.current) return;
      if (eventSourceRef.current !== es) return; // superseded socket
      reconnectAttemptsRef.current = 0;
      gaveUpRef.current = false;
      setStreamActive(true);
      armWatchdog(generation, es);
    });

    es.addEventListener('unread-count', (event: MessageEvent) => {
      if (generation !== generationRef.current) return;
      if (eventSourceRef.current !== es) return; // superseded socket
      armWatchdog(generation, es);
      try {
        const data = JSON.parse(event.data);
        if (data && typeof data.count === 'number') {
          onUnreadCountRef.current(data.count);
        }
      } catch (err) {
        console.error('Failed to parse unread-count event:', err);
      }
    });

    // Keepalive ping — its only job is proving liveness to the watchdog.
    es.addEventListener('ping', () => {
      if (generation !== generationRef.current) return;
      if (eventSourceRef.current !== es) return; // superseded socket
      armWatchdog(generation, es);
    });

    // Browser-level connection error — covers the deploy-skew 404 (old
    // backend without the stream route errors immediately) and any
    // mid-flight drop. Close, flip the poll back on, retry with backoff.
    es.onerror = () => {
      es.close();
      if (generation !== generationRef.current) return;
      if (eventSourceRef.current === es) {
        eventSourceRef.current = null;
      }
      clearWatchdog();
      setStreamActive(false);
      scheduleReconnect(generation);
    };

    // Arm from open: a server that never says anything is dead too.
    armWatchdog(generation, es);
  }, [armWatchdog, clearWatchdog, scheduleReconnect]);

  connectRef.current = connect;

  const disconnect = useCallback(() => {
    // Invalidate every in-flight continuation and pending timer first
    // (#555 review, finding 1).
    generationRef.current += 1;
    clearReconnectTimer();
    clearWatchdog();
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    setStreamActive(false);
  }, [clearReconnectTimer, clearWatchdog]);

  useEffect(() => {
    if (!enabled) return;
    connect();
    return () => disconnect();
  }, [enabled, connect, disconnect]);

  // Hidden-tab teardown + visible reconnect (#555 review, finding 7).
  useEffect(() => {
    if (!enabled) return undefined;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (hiddenTimeoutRef.current) return; // countdown already running
        hiddenTimeoutRef.current = setTimeout(() => {
          hiddenTimeoutRef.current = null;
          // Hidden >60s: close the stream. react-query pauses background
          // polls too, so the hidden tab now generates zero traffic
          // (D-W2-22 invariant, #504 class).
          disconnect();
        }, HIDDEN_CLOSE_DELAY);
        return;
      }

      // Visible again.
      if (hiddenTimeoutRef.current) {
        clearTimeout(hiddenTimeoutRef.current);
        hiddenTimeoutRef.current = null;
      }
      if (!eventSourceRef.current) {
        // Reconnect immediately. A stream torn down while hidden gets a
        // fresh attempt budget. A stream that had given up gets one try and
        // no retries (#2382): if the server confirms it, `connected` resets
        // the budget; if not, it stays given up until the next return.
        clearReconnectTimer();
        reconnectAttemptsRef.current = gaveUpRef.current ? MAX_RECONNECT_ATTEMPTS : 0;
        connect();
      }
      // Let the owner refetch the badge once (poll was paused while hidden
      // and refetchOnWindowFocus is globally disabled).
      onVisibleRef.current?.();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (hiddenTimeoutRef.current) {
        clearTimeout(hiddenTimeoutRef.current);
        hiddenTimeoutRef.current = null;
      }
    };
  }, [enabled, connect, disconnect, clearReconnectTimer]);

  return { streamActive };
}
